import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import "./RecipeList.css";
import { AuthTokenRetrieve, LoginButton, LogoutButton } from "../Utility/AuthTokenRetrieve";
import { apiFetch } from "../Utility/api";

const RECIPE_TYPES = ["All", "Mexican", "Pasta", "Dessert", "Misc"];

const toFormRecipe = (recipe) => ({
  Id: recipe?.Id || "",
  Name: recipe?.Name || "",
  Link: recipe?.Link || "",
  Type: recipe?.Type || "All",
  Author: recipe?.Author || "",
  EstimatedTime: recipe?.EstimatedTime || recipe?.estimatedTime || "",
  Ingredients: recipe?.Ingredients || "",
  Instructions: recipe?.Instructions || "",
});

const EditRecipe = () => {
  const { data } = useOutletContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { shouldShow: shouldShowGoogle } = AuthTokenRetrieve();
  const [shouldShow, setShouldShow] = useState(false);
  const [inputs, setInputs] = useState(toFormRecipe());
  const [statusText, setStatusText] = useState("");
  const [errorText, setErrorText] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const selectedRecipe = useMemo(() => {
    const recipes = data || [];
    const recipeId = searchParams.get("Id");
    const recipeName = searchParams.get("Recipe");

    if (recipeId) {
      return recipes.find((recipe) => String(recipe.Id) === recipeId);
    }

    if (recipeName) {
      return recipes.find((recipe) => recipe.Name === recipeName);
    }

    return null;
  }, [data, searchParams]);

  const hasRecipeSelection = !!searchParams.get("Id") || !!searchParams.get("Recipe");

  useEffect(() => {
    setShouldShow(shouldShowGoogle);
  }, [shouldShowGoogle]);

  useEffect(() => {
    if (selectedRecipe) {
      setInputs(toFormRecipe(selectedRecipe));
      setStatusText("");
      setErrorText("");
    }
  }, [selectedRecipe]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setIsSaving(true);
    setStatusText("");
    setErrorText("");

    apiFetch(`/food/recipes/${inputs.Id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ inputs }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Recipe save failed");
        }
        return response.json();
      })
      .then((data) => {
        setInputs(toFormRecipe(data));
        setStatusText("Recipe saved.");
      })
      .catch(() => setErrorText("Could not save this recipe."))
      .finally(() => setIsSaving(false));
  };

  if (!shouldShow) {
    return (
      <div className="RecipeEditPage">
        <LoginButton setShouldShow={setShouldShow}></LoginButton>
      </div>
    );
  }

  if (data && !hasRecipeSelection) {
    return (
      <div className="RecipeEditPage searchBarDiv formViewWrapper">
        <div className="RecipeEditSelectWrapper">
          <LogoutButton setShouldShow={setShouldShow}></LogoutButton>
          <div className="inputDiv RecipeEditSelectInput">
            <label>Recipe</label>
            <select
              className="inputBox addInput"
              value=""
              onChange={(event) => navigate(`/food/edit?Id=${event.target.value}`)}
            >
              <option value="" disabled>
                Select a recipe
              </option>
              {(data || []).map((recipe) => (
                <option key={recipe.Id} value={recipe.Id}>
                  {recipe.Name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    );
  }

  if (data && !selectedRecipe && !inputs.Id) {
    return (
      <div className="RecipeEditPage searchBarDiv formViewWrapper">
        <h2>Recipe not found</h2>
        <Link className="summaryButton RecipeEditWideButton" to="/food">
          Food Home
        </Link>
      </div>
    );
  }

  return (
    <div className="RecipeEditPage">
      <div className="searchBarDiv formViewWrapper">
        <form onSubmit={handleSubmit} className="RecipeEditForm">
          <div className="RecipeEditActionRow">
            <LogoutButton setShouldShow={setShouldShow}></LogoutButton>
            <Link
              className="summaryButton RecipeEditWideButton"
              to={inputs.Name ? `/food/view?Recipe=${encodeURIComponent(inputs.Name)}` : "/food"}
            >
              View
            </Link>
            <button className="summaryButton RecipeEditWideButton" type="submit" disabled={isSaving || !inputs.Id}>
              {isSaving ? "Saving" : "Save"}
            </button>
          </div>

          {statusText && <p className="RecipeEditStatus">{statusText}</p>}
          {errorText && <p className="RecipeEditError">{errorText}</p>}

          <div className="inputSingleForm RecipeEditGrid">
            <div className="inputDiv">
              <label>Name</label>
              <input
                type="text"
                name="Name"
                className="inputBox addInput"
                value={inputs.Name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="inputDiv">
              <label>Link</label>
              <input
                type="text"
                name="Link"
                className="inputBox addInput"
                value={inputs.Link}
                onChange={handleChange}
              />
            </div>
            <div className="inputDiv">
              <label>Type</label>
              <select
                name="Type"
                className="inputBox addInput"
                value={inputs.Type}
                onChange={handleChange}
              >
                {RECIPE_TYPES.map((recipeType) => (
                  <option key={recipeType}>{recipeType}</option>
                ))}
              </select>
            </div>
            <div className="inputDiv">
              <label>Author</label>
              <input
                type="text"
                name="Author"
                className="inputBox addInput"
                value={inputs.Author}
                onChange={handleChange}
              />
            </div>
            <div className="inputDiv">
              <label>Estimated Time</label>
              <input
                type="text"
                name="EstimatedTime"
                className="inputBox addInput"
                value={inputs.EstimatedTime}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="inputDiv textAreaDiv RecipeEditTextareaDiv">
            <label>Ingredients</label>
            <textarea
              name="Ingredients"
              className="inputBox textAreaAdd"
              value={inputs.Ingredients}
              onChange={handleChange}
            />
          </div>
          <div className="inputDiv textAreaDiv RecipeEditTextareaDiv">
            <label>Instructions</label>
            <textarea
              name="Instructions"
              className="inputBox textAreaAdd"
              value={inputs.Instructions}
              onChange={handleChange}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRecipe;
